'use client'

import type { CSSProperties, ReactNode } from 'react'
import Link from 'next/link'
import type { Route } from 'next'
import { useRouter } from 'next/navigation'
import { useClerk, useUser } from '@clerk/nextjs'
import {
  LayoutGrid,
  ListChecks,
  LogOut,
  MoreVertical,
  PhoneCall,
  ShieldCheck,
  User,
  UserCog,
  Users,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar'
import { Separator } from '@/components/ui/separator'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Logo } from '@/components/brand/logo'
import { ModeToggle } from '@/components/mode-toggle'
import { NotificationBell } from '@/components/notification-bell'
import { QueryErrorState } from '@/components/patterns/queryErrorState'
import { Button } from '@/components/ui/button'
import { routes } from '@/config/routes'
import { useMe } from '@/features/session'
import { AppError } from '@/lib/errors/appError'
import { useThemeStore } from '@/stores/theme-store'
import '@/components/patterns/dashboard-shell.css'

export type AdminTab = 'overview' | 'cases' | 'staff' | 'calls' | 'investors' | 'profile'

const NAV_ITEMS: { tab: AdminTab; label: string; href: Route; icon: typeof Users }[] = [
  { tab: 'overview', label: 'Overview', href: '/admin', icon: LayoutGrid },
  { tab: 'cases', label: 'Cases', href: '/admin/cases', icon: ListChecks },
  { tab: 'staff', label: 'Staff', href: '/admin/staff', icon: UserCog },
  { tab: 'calls', label: 'Calls', href: '/admin/calls', icon: PhoneCall },
  { tab: 'investors', label: 'Investors', href: '/admin/investors', icon: Users },
]

const TAB_TITLE: Record<AdminTab, string> = {
  overview: 'Overview',
  cases: 'Cases',
  staff: 'Staff',
  calls: 'Calls',
  investors: 'Investors',
  profile: 'Profile',
}

function getSidebarCookieDefault(): boolean {
  if (typeof document === 'undefined') return true
  const match = document.cookie.match(/(?:^|; )sidebar_state=([^;]+)/)
  return match ? match[1] === 'true' : true
}

function initials(name: string | null | undefined): string {
  if (!name) return 'AD'
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('')
}

function AdminUserMenu() {
  const router = useRouter()
  const { isMobile } = useSidebar()
  const { user } = useUser()
  const { signOut } = useClerk()

  const name = user?.fullName ?? 'Admin'

  const handleSignOut = async () => {
    await signOut({ redirectUrl: '/team/login' })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton
          size="lg"
          className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground group-data-[collapsible=icon]:justify-center"
        >
          <Avatar className="h-8 w-8 shrink-0 rounded-lg">
            <AvatarFallback className="bg-primary text-primary-foreground rounded-lg text-[11px] font-bold">
              {initials(name)}
            </AvatarFallback>
          </Avatar>
          <span className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
            <span className="truncate font-medium">{name}</span>
            <span className="text-muted-foreground truncate text-xs">Administrator</span>
          </span>
          <MoreVertical className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
        side={isMobile ? 'bottom' : 'right'}
        align="end"
        sideOffset={4}
      >
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
            <Avatar className="h-8 w-8 rounded-lg">
              <AvatarFallback className="bg-primary text-primary-foreground rounded-lg text-[11px] font-bold">
                {initials(name)}
              </AvatarFallback>
            </Avatar>
            <span className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium">{name}</span>
              <span className="text-muted-foreground truncate text-xs">Administrator</span>
            </span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => router.push('/admin/profile')}>
          <User /> View Profile
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onClick={handleSignOut}>
          <LogOut /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function AdminShell({
  active,
  title,
  description,
  actions,
  children,
  fullHeight,
  fitViewport,
}: {
  active: AdminTab
  title?: ReactNode
  description?: ReactNode
  actions?: ReactNode
  children: ReactNode
  /** Page manages its own internal scrolling (e.g. a sticky card that fills the
   * viewport) — size the content area to exactly the available height instead
   * of letting it grow and scroll the whole page. */
  fullHeight?: boolean
  /** Page is meant to be read in one screen — size the content area to exactly the
   * viewport from `md` up, so a page whose blocks can flex fills the screen without
   * scrolling. Unlike `fullHeight` the scroll container keeps `overflow-y-auto`, so a
   * window too short for the page's own minimums still scrolls rather than clipping,
   * and below `md` the page scrolls normally — the mobile bottom nav needs that room. */
  fitViewport?: boolean
}) {
  const router = useRouter()
  const { signOut } = useClerk()
  const theme = useThemeStore((s) => s.theme)
  const me = useMe()
  // A brand-new admin (created in the Clerk Dashboard) can briefly have a valid session before
  // their local row exists — same fallback StaffShell shows, instead of every admin page erroring.
  const meNotSynced =
    me.isError && me.error instanceof AppError && me.error.code === 'RESOURCE_NOT_FOUND'

  return (
    <SidebarProvider
      defaultOpen={getSidebarCookieDefault()}
      // `dark` itself is applied once, at `<html>` (see `src/providers/theme-provider.tsx`) — Radix
      // portals (Dialog/DropdownMenu/Popover/etc.) mount under `document.body`, a sibling of this
      // div, so a class here would never reach them.
      className={cn(
        'text-foreground h-svh overflow-hidden',
        theme === 'dark' ? 'bg-background' : 'bg-muted',
      )}
      style={{ '--sidebar-width': '16rem', '--header-height': '3.5rem' } as CSSProperties}
    >
      <Sidebar collapsible="icon" variant="inset" className="hidden md:flex">
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton asChild className="data-[slot=sidebar-menu-button]:p-1.5!">
                <Link href="/admin" aria-label="Nivex admin home">
                  <Logo size={20} />
                  <span className="text-base font-semibold">Nivex Admin</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            <SidebarMenu>
              {NAV_ITEMS.map((item) => (
                <SidebarMenuItem key={item.tab}>
                  <SidebarMenuButton asChild isActive={active === item.tab} tooltip={item.label}>
                    <Link href={item.href}>
                      <item.icon />
                      <span>{item.label}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter>
          <div className="bg-muted/40 mx-1 mb-1 rounded-lg border p-3 group-data-[collapsible=icon]:hidden">
            <p className="flex items-center gap-1.5 text-[13px] font-semibold">
              <ShieldCheck className="text-primary size-3.5" /> Admin console
            </p>
            <p className="text-muted-foreground mt-0.5 text-[11px] leading-relaxed">
              Changes here are visible to investors immediately.
            </p>
          </div>
          <AdminUserMenu />
        </SidebarFooter>
      </Sidebar>

      <SidebarInset className="min-w-0 overflow-hidden">
        <header className="h-(--header-height) flex shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear">
          <div className="flex w-full min-w-0 items-center gap-1 px-4 lg:gap-2 lg:px-6">
            <SidebarTrigger className="-ml-1 hidden md:flex" />
            <Separator
              orientation="vertical"
              className="mx-2 hidden data-[orientation=vertical]:h-4 md:block"
            />

            <Link
              href="/admin"
              aria-label="Nivex admin home"
              className="flex shrink-0 items-center gap-2 md:hidden"
            >
              <Logo size={20} />
              <span className="text-base font-semibold">Nivex Admin</span>
            </Link>

            <h1 className="hidden shrink-0 text-base font-medium md:block">{TAB_TITLE[active]}</h1>

            <div className="ml-auto flex items-center gap-2">
              <ModeToggle />
              <NotificationBell clientDetailHref={routes.admin.clientDetail} />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button aria-label="Admin menu" className="flex md:hidden">
                    <Avatar className="h-8 w-8 rounded-full">
                      <AvatarFallback className="bg-primary text-primary-foreground rounded-full text-xs font-bold">
                        AD
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => router.push('/admin')}>
                    <LayoutGrid /> Overview
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => router.push('/admin/profile')}>
                    <User /> Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    variant="destructive"
                    onClick={() => void signOut({ redirectUrl: '/team/login' })}
                  >
                    <LogOut /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <div
          className={cn(
            'flex min-w-0 flex-1 flex-col overflow-y-auto',
            // The fade is a static mask on the container's own edges, not scroll-position-aware — it
            // always fades the bottom ~24px regardless of whether that's mid-scroll or the true end of
            // content. `fitViewport` pages are meant to fit exactly one screen, so their true final
            // content (a bordered footer card, not a plain table row) sits right at that edge and the
            // permanent fade makes it look sliced off. Skip the fade there; keep it everywhere else,
            // where the "more below" cue is genuinely useful (long tables that always overflow).
            !fitViewport && 'dash-scroll-fade',
            // `fullHeight` only swaps in the fixed-viewport, internally-scrolling split-pane layout
            // once there's room for it (`@4xl/main` and up) — below that, the page falls back to this
            // same normal document scroll every other screen gets, since there's no room on a phone
            // for a fixed split pane and content would otherwise be clipped with no way to reach it.
            fullHeight && '@4xl/main:min-h-0 @4xl/main:overflow-hidden',
          )}
        >
          <div
            className={cn(
              '@container/main flex min-w-0 flex-1 flex-col',
              fullHeight && '@4xl/main:min-h-0',
              fitViewport && 'md:min-h-0',
            )}
          >
            <div
              className={cn(
                'flex min-w-0 flex-col gap-4 px-4 py-4 pb-28 md:gap-6 md:py-6 md:pb-6 lg:px-6',
                fullHeight && '@4xl/main:min-h-0 @4xl/main:flex-1',
                // A touch more breathing room than the md:pb-6 baseline — on a fitViewport page the
                // last thing on screen is a bordered footer card, not a plain table row, so sitting
                // flush against the viewport edge reads as cramped rather than intentional.
                fitViewport && 'md:min-h-0 md:flex-1 md:pb-10',
              )}
            >
              {title || actions ? (
                <div className="flex flex-wrap items-start justify-between gap-3">
                  {title ? (
                    <div>
                      <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
                      {description ? (
                        <p className="text-muted-foreground mt-1 text-sm">{description}</p>
                      ) : null}
                    </div>
                  ) : (
                    <div />
                  )}
                  {actions}
                </div>
              ) : null}
              {me.isError ? (
                meNotSynced ? (
                  <div className="border-border bg-card flex min-h-40 flex-col items-center justify-center gap-2 rounded-xl border p-6 text-center">
                    <p className="text-foreground text-sm font-medium">
                      Your account is still being set up
                    </p>
                    <p className="text-muted-foreground text-xs">
                      Check back in a moment, or contact an admin if this persists.
                    </p>
                    <Button size="sm" variant="outline" onClick={() => void me.refetch()}>
                      Try again
                    </Button>
                  </div>
                ) : (
                  <QueryErrorState error={me.error} onRetry={() => void me.refetch()} />
                )
              ) : fullHeight ? (
                <div className="@4xl/main:min-h-0 @4xl/main:flex-1 flex flex-col">{children}</div>
              ) : (
                children
              )}
            </div>
          </div>
        </div>
      </SidebarInset>

      <nav
        className={cn(
          'bg-background fixed bottom-4 left-1/2 z-30 flex h-[60px] w-[calc(100%-2rem)] max-w-[420px] -translate-x-1/2 items-center gap-0.5 rounded-[20px] border p-1.5 shadow-lg md:hidden',
        )}
      >
        {NAV_ITEMS.map((item) => {
          const isActive = active === item.tab
          return (
            <Link
              key={item.tab}
              href={item.href}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                'text-muted-foreground flex h-12 min-w-0 flex-1 items-center justify-center gap-1.5 overflow-hidden rounded-2xl transition-[flex,background-color,color] duration-200',
                isActive && 'bg-primary text-primary-foreground flex-[2]',
              )}
            >
              <item.icon className="h-[18px] w-[18px] shrink-0" strokeWidth={2} />
              {isActive && <span className="truncate text-xs font-semibold">{item.label}</span>}
            </Link>
          )
        })}
      </nav>
    </SidebarProvider>
  )
}
